import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify';
import labelKey from "../../localization/label.json"
import { KTSVG } from '../../../_metronic/helpers';
import { Alert, Modal } from 'react-bootstrap';
import { useUpdateRequestUserStatusMutation } from '../../../services/authApi';
import SearchSelect from '../../../components/textFields/SearchSelect';

type Props = {
    show: boolean
    handleClose: () => void
    data: any
}

type Option = {
    value: string;
    label: string;
}

const UpdateUserStatusModal = ({ show, handleClose, data }: Props) => {
    const [mgrActionStatus, setMgrActionStatus] = useState<string | null>(null);
    const [comments, setComments] = useState<string>('');
    const [statusError, setStatusError] = useState<string>('');
    const [commentsError, setCommentsError] = useState<string>('');
    const [accountActiveDeActive, { isLoading, isSuccess, isError, error }] = useUpdateRequestUserStatusMutation();

    const userDisplayName = `${data?.firstName || ''} ${data?.lastName || ''}`.trim() || data?.userEmail || 'User';

    const baseDropdownOptions = [
        { id: "2", value: 'Approved' },
        { id: "3", value: 'Rejected' },
    ];

    const dropdownOptions = baseDropdownOptions.filter(
        option => option.id !== data?.userApprovalStatus?.id?.toString()
    );

    const handleConfirmation = async () => {
        let hasError = false;

        if (!mgrActionStatus || mgrActionStatus === '0') {
            setStatusError('Please select a status');
            hasError = true;
            toast.error('Please select a status');
        }

        if (mgrActionStatus == "3" && !comments.trim()) {
            setCommentsError('Comments are required when rejecting');
            hasError = true;
            if (!statusError) toast.error('Please add comments for rejection');
        }

        if (hasError) return;

        await accountActiveDeActive({
            draftUserId: data?.userDraftID || data?.id,
            approvalStatus: {
                "id": parseInt(mgrActionStatus!),
                "value": ""
            },
            comments: comments?.trim() || null
        });
        handleClose();
    };

    const handleStatusChange = (e: any) => {
        if (e) {
            setMgrActionStatus(e.value);
            if (statusError) setStatusError('');
            if (e.value === "2" && commentsError) setCommentsError('');
        } else {
            setMgrActionStatus('0');
            setStatusError('Please select a status');
        }
    };

    const handleCommentsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setComments(e.target.value);
        if (commentsError) setCommentsError('');
    };

    useEffect(() => {
        if (isSuccess) {
            toast.success('Status Update Successfully');
            handleClose();
            setMgrActionStatus(null);
            setComments('');
            setStatusError('');
            setCommentsError('');
        }
    }, [isSuccess]);

    useEffect(() => {
        if (isError && error) {
            let errorMessage: string = 'Something went wrong';
            if ('data' in error && error.data && typeof error.data === 'object' && 'message' in error.data) {
                errorMessage = (error.data as { message: string }).message;
            }
            toast.error(errorMessage);
        }
    }, [isError, error]);

    useEffect(() => {
        if (!show) {
            setMgrActionStatus(null);
            setComments('');
            setStatusError('');
            setCommentsError('');
        }
    }, [show]);

    const getValueObject = (): Option | null => {
        if (mgrActionStatus && mgrActionStatus !== '0') {
            const foundOption = baseDropdownOptions.find((option: any) => option.id === mgrActionStatus);
            if (foundOption && foundOption.value) {
                return {
                    value: mgrActionStatus,
                    label: foundOption.value
                };
            }
        }
        return null;
    };

    const getOptions = (): Option[] => {
        if (Array.isArray(dropdownOptions)) {
            return dropdownOptions.map((option: any) => ({
                value: option.id,
                label: option.value || '',
            }));
        }
        return [];
    };

    const isCommentsRequired = mgrActionStatus === "3";
    const isSubmitDisabled = isLoading || !mgrActionStatus || (isCommentsRequired && !comments.trim());

    return (
        <Modal
            aria-hidden='true'
            dialogClassName='modal-dialog modal-md modal-dialog-centered'
            show={show}
            onHide={handleClose}
        >
            <div className='modal-header bg-light py-4'>
                <h2 className='mb-0'>Update User Status ({userDisplayName})</h2>
                <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={handleClose}>
                    <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
                </div>
            </div>
            <div className='modal-body'>
                <Alert variant="primary" className="mb-5">
                    <p className='fs-7 mb-0'>
                        Select <b>Approved</b> to create the SpendSail user account for this request.
                        Select <b>Rejected</b> to decline the onboarding request (comments required).
                        If a corporate credit card was requested and you approve, a CC request is opened and routed to higher management by division.
                    </p>
                </Alert>

                <div className='col-md-12 col-lg-12 mb-5'>
                    <label className='form-label fw-bold text-dark fs-6 mb-2'>
                        {labelKey.status}:
                        <span className="text-danger"> *</span>
                    </label>
                    <SearchSelect
                        options={getOptions()}
                        placeholder="Select status..."
                        value={getValueObject()}
                        onChange={handleStatusChange}
                    />
                    {statusError && (
                        <div className="text-danger mt-1 fs-7">
                            {statusError}
                        </div>
                    )}
                    {mgrActionStatus === "2" && (
                        <div className="text-success mt-2 fs-7">
                            Approving will create the user account and continue the onboarding workflow.
                        </div>
                    )}
                    {mgrActionStatus === "3" && (
                        <div className="text-danger mt-2 fs-7">
                            Rejecting will decline this user request. Please provide a clear reason in comments.
                        </div>
                    )}
                </div>

                <div className='col-md-12 col-lg-12'>
                    <label className='form-label fw-bold text-dark fs-6 mb-2'>
                        {labelKey.comments || 'Comments'}:
                        {isCommentsRequired && <span className="text-danger"> *</span>}
                    </label>
                    <textarea
                        className={`form-control form-control-sm form-control-solid ${commentsError ? 'border-danger' : 'border-0'} p-3 resize-none min-h-100px`}
                        rows={4}
                        placeholder={isCommentsRequired ? 'Please provide comments for rejection...' : 'Add comments (optional)...'}
                        value={comments}
                        onChange={handleCommentsChange}
                        disabled={!mgrActionStatus}
                    />
                    {commentsError && (
                        <div className="text-danger mt-1 fs-7">
                            {commentsError}
                        </div>
                    )}
                    {isCommentsRequired && !commentsError && (
                        <div className="text-muted mt-1 fs-7">
                            Comments are required when rejecting
                        </div>
                    )}
                    {mgrActionStatus === "2" && (
                        <div className="text-muted mt-1 fs-7">
                            Comments are optional for approval
                        </div>
                    )}
                </div>
            </div>
            <div className='modal-footer justify-content-center py-4'>
                <button className='btn btn-light' onClick={handleClose}>{labelKey.cancel}</button>
                <button
                    type='submit'
                    id='kt_sign_up_submit'
                    className='btn btn-primary'
                    onClick={handleConfirmation}
                    disabled={isSubmitDisabled}
                >
                    {!isLoading && <span className='indicator-label'>Update user request status</span>}
                    {isLoading && (
                        <span className='indicator-progress' style={{ display: 'block' }}>
                            {labelKey.pleaseWait}{' '}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                    )}
                </button>
            </div>
        </Modal>
    )
}

export default UpdateUserStatusModal
